/**
 * Logic evaluation — one order, imported by everything that needs it.
 *
 * DECISION-034: `logic[]` is evaluated **per step**. Within each step's group,
 * rules run in array order and the first whose condition matches wins; the
 * winning rules across steps all apply. A rule with no `step_id` forms its own
 * group and is evaluated on its own terms.
 *
 * That is what `design/screens/editor-logic.html` means by "Rules run in
 * order. The first matching rule wins" — the screen groups its four rules
 * under `Step 1 · Welcome 1 rule`, `Step 2 · Budget 2 rules`, `Step 3 ·
 * Timeline and sign-off 1 rule`, and a reading in which only one of the four
 * ever fires makes that example incoherent.
 *
 * This module exists so there is exactly one implementation of that order. PRD
 * §3.4 calls builder/runtime divergence "the defining bug class of every form
 * builder ever shipped"; the embed runtime (Story 5.2), the Logic tab's
 * reachability warning (Story 6.4) and the calibration sampler (Story 2.4) all
 * read from here rather than each deciding for itself.
 */
import type { Funnel, LogicRule } from './funnel'
import { evaluateCondition, type ScoringLookup } from './scoring'

/**
 * WHICH GROUP A RULE COMPETES IN — the one definition of DECISION-034's scope.
 *
 * An anchorless rule is its own group, so it is never suppressed by a rule it
 * has no stated relationship to. The `rule:` prefix cannot collide with a step
 * id, which the schema constrains to a plain identifier.
 *
 * Exported because Story 6.4's Logic tab numbers rules "in run order" and has
 * to group them the same way this does. That is one line, and one line is
 * exactly how PRD §3.4's "defining bug class of every form builder ever
 * shipped" starts: the builder and the runtime each keeping their own copy of
 * the order, agreeing today and diverging the first time either is touched.
 */
export const logicGroupOf = (rule: LogicRule): string => rule.step_id ?? `rule:${rule.id}`

/**
 * The rules that fire for one set of answers — at most one per step group,
 * in funnel order.
 */
export function winningRules(funnel: Funnel, lookup: ScoringLookup): LogicRule[] {
  const winners: LogicRule[] = []
  const decided = new Set<string>()

  for (const rule of funnel.logic) {
    const group = logicGroupOf(rule)
    if (decided.has(group)) continue
    if (!evaluateCondition(rule.when, lookup)) continue
    decided.add(group)
    winners.push(rule)
  }

  return winners
}

export type LogicOutcome = {
  /** Blocks the respondent never sees — hidden outright, or on a skipped step. */
  hiddenBlocks: Set<string>
  skippedSteps: Set<string>
  disqualified: boolean
  /** Set by `set_hidden_field`, keyed by the hidden_field block's id. */
  hiddenFieldValues: Map<string, string>
  applied: LogicRule[]
}

/**
 * What the winning rules do, together.
 *
 * Two things are deliberately resolved toward doing less. A `skip_to` with no
 * anchoring step skips nothing, because there is no way to know what was
 * jumped over. And an explicit `show` beats a `hide` on the same block, so a
 * block whose visibility two steps disagree about stays visible — over-hiding
 * would drop a respondent's answer, and drop it out of the calibration
 * distribution, without saying so. Neither case can arise at M1: nothing is
 * hidden by default, so `show` has nothing to undo.
 */
export function logicOutcome(funnel: Funnel, lookup: ScoringLookup): LogicOutcome {
  const applied = winningRules(funnel, lookup)

  const hiddenBlocks = new Set<string>()
  const shown = new Set<string>()
  const skippedSteps = new Set<string>()
  const hiddenFieldValues = new Map<string, string>()
  let disqualified = false

  for (const rule of applied) {
    const effect = rule.then
    switch (effect.action) {
      case 'hide':
        hiddenBlocks.add(effect.target_block_id)
        break

      case 'show':
        shown.add(effect.target_block_id)
        break

      case 'disqualify':
        disqualified = true
        break

      case 'set_hidden_field':
        hiddenFieldValues.set(effect.target_block_id, effect.value)
        break

      case 'skip_to': {
        const target = funnel.steps.findIndex((step) => step.id === effect.target_step_id)
        const from = funnel.steps.findIndex((step) => step.id === rule.step_id)
        if (target > -1 && from > -1) {
          for (const step of funnel.steps.slice(from + 1, target)) {
            skippedSteps.add(step.id)
            for (const blockId of step.blocks) hiddenBlocks.add(blockId)
          }
        }
        break
      }
    }
  }

  for (const blockId of shown) hiddenBlocks.delete(blockId)

  return { hiddenBlocks, skippedSteps, disqualified, hiddenFieldValues, applied }
}
